import { useState } from 'preact/hooks';
import { allQuests } from '../../game/quests';
import { registry } from '../../sim/registry';
import { ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import {
  Bar,
  Empty,
  Rewards,
  TipLayer,
  commit,
  timeAgo,
  toast,
  useCtx,
  useNow,
} from '../economy/ui';
import { layoutOrNull, overworldLayouts } from '../travel/world';
import {
  type AchievementCategory,
  type AchievementState,
  achievementStates,
  achievementTitles,
  ACHIEVEMENT_LEGACY_XP,
} from './achievements';
import {
  JOURNAL_MILESTONES,
  type JournalCategoryId,
  type ZoneJournal,
  claimMilestone,
  journalTitle,
  journalTitles,
  milestoneClaimed,
  milestoneReward,
  zoneJournal,
} from './journal';
import { setActiveTitle } from './titles';

type Tab = 'journal' | 'achievements' | 'titles';

const CAT_GLYPH: Record<JournalCategoryId, string> = {
  seeds: '✦',
  hearts: '♥',
  vistas: '◈',
  lore: '❡',
  bosses: '☠',
  dungeons: '⛫',
  quests: '◆',
};

function useJournals(): ZoneJournal[] {
  const ctx = useCtx();
  if (!ctx) return [];
  const quests = allQuests();
  const monsterName = (id: string) => registry.monsters.get(id)?.name ?? id;
  return overworldLayouts()
    .filter(
      (l) => l.kind === 'field' || l.kind === 'town' || l.kind === 'island' || l.kind === 'haven',
    )
    .map((l) => zoneJournal(l, ctx.a, ctx.c, { dungeon: layoutOrNull, quests, monsterName }));
}

function Ring({ pct }: { pct: number }) {
  const r = 34;
  const len = 2 * Math.PI * r;
  return (
    <svg class="jn-ring" viewBox="0 0 84 84" width="84" height="84" aria-label={`${pct}% complete`}>
      <circle cx="42" cy="42" r={r} fill="none" stroke="#1a1d20" stroke-width="7" />
      <circle
        cx="42"
        cy="42"
        r={r}
        fill="none"
        stroke="url(#jn-grad)"
        stroke-width="7"
        stroke-linecap="round"
        stroke-dasharray={`${(len * pct) / 100} ${len}`}
        transform="rotate(-90 42 42)"
      />
      <defs>
        <linearGradient id="jn-grad" x1="0" x2="1">
          <stop offset="0" stop-color="#836d22" />
          <stop offset="1" stop-color="#fae4b2" />
        </linearGradient>
      </defs>
      <text x="42" y="47" text-anchor="middle" class="jn-ring-t">
        {pct}%
      </text>
    </svg>
  );
}

function JournalTab() {
  const ctx = useCtx();
  const journals = useJournals();
  const [sel, setSel] = useState<string | null>(null);
  const [open, setOpen] = useState<JournalCategoryId | null>(null);
  if (!ctx) return null;
  if (!journals.length)
    return <Empty title="The journal is blank">Explore the Sundered Isles to fill it.</Empty>;
  const j = journals.find((x) => x.zone === (sel ?? ctx.c.zone)) ?? journals[0]!;
  return (
    <div class="jn-journal">
      <nav class="jn-zones fx-scroll">
        {journals.map((z) => (
          <button
            key={z.zone}
            class={`jn-zone${z.zone === j.zone ? ' is-sel' : ''}`}
            onClick={() => setSel(z.zone)}
          >
            <span class="jn-zone-n">{z.name}</span>
            <span class="jn-zone-l">
              {z.kind === 'town' ? 'City' : `Lv ${z.levelRange[0]}–${z.levelRange[1]}`}
            </span>
            <Bar value={z.pct} max={100} tone="gold" />
            <span class="jn-zone-p">{z.pct}%</span>
          </button>
        ))}
      </nav>
      <section class="jn-detail fx-scroll">
        <header class="jn-head">
          <Ring pct={j.pct} />
          <div>
            <h3>{j.name}</h3>
            <div class="fx-sub">
              {j.have} of {j.total} discoveries ·{' '}
              {j.kind === 'town' ? 'City' : `Levels ${j.levelRange[0]}–${j.levelRange[1]}`}
            </div>
          </div>
        </header>
        <div class="jn-rail">
          <span class="fx-h">Journal rewards</span>
          <div class="jn-rail-track">
            <Bar value={j.pct} max={100} tone="gold" />
            {JOURNAL_MILESTONES.map((m) => (
              <i
                key={m.pct}
                class={`jn-tick${j.pct >= m.pct ? ' is-on' : ''}`}
                style={{ left: `${m.pct}%` }}
              />
            ))}
          </div>
          <div class="jn-miles">
            {JOURNAL_MILESTONES.map((m) => {
              const claimed = milestoneClaimed(ctx.a, j.zone, m.pct);
              const reached = j.pct >= m.pct;
              const r = milestoneReward(j, m.pct);
              return (
                <div
                  key={m.pct}
                  class={`jn-mile${claimed ? ' is-claimed' : reached ? ' is-ready' : ''}`}
                >
                  <b>{m.pct}%</b>
                  <Rewards silver={r.silver} gold={r.gold} items={r.items} title={r.title} />
                  {claimed ? (
                    <span class="jn-mile-s">Claimed</span>
                  ) : (
                    <button
                      class="btn btn--gold sm"
                      disabled={!reached}
                      onClick={() => {
                        const got = claimMilestone(ctx.p, j, m.pct);
                        if (got) {
                          toast(
                            `${j.name} ${m.pct}% reward claimed${got.title ? ` — title “${got.title}”` : ''}`,
                            '#eacd95',
                          );
                          commit();
                        }
                      }}
                    >
                      Claim
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div class="jn-cats">
          {j.categories.map((cat) => (
            <div key={cat.id} class={`jn-cat${open === cat.id ? ' is-open' : ''}`}>
              <button
                class="jn-cat-row"
                onClick={() => setOpen(open === cat.id ? null : cat.id)}
                aria-expanded={open === cat.id}
              >
                <span class="jn-glyph">{CAT_GLYPH[cat.id]}</span>
                <span class="jn-cat-n">{cat.name}</span>
                <Bar
                  value={cat.have}
                  max={cat.total}
                  tone={cat.have === cat.total ? 'green' : 'gold'}
                />
                <span class="jn-cat-c">
                  {cat.have}/{cat.total}
                </span>
              </button>
              {open === cat.id && (
                <ul class="jn-entries">
                  {cat.entries.map((e) => (
                    <li key={e.id} class={e.done ? 'is-done' : ''}>
                      <i />
                      {cat.id === 'seeds' && !e.done
                        ? 'Undiscovered Glimmerseed'
                        : e.done || cat.id === 'quests' || cat.id === 'dungeons'
                          ? e.name
                          : '???'}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const CATS: (AchievementCategory | 'All')[] = [
  'All',
  'Combat',
  'Progression',
  'Collection',
  'Adventure',
];

function AchievementRow({ s, now }: { s: AchievementState; now: number }) {
  const d = s.def;
  return (
    <div class={`jn-ach${s.done ? ' is-done' : ''}`}>
      <span class="jn-medal" aria-hidden="true" />
      <div class="jn-ach-m">
        <div class="jn-ach-n">{d.name}</div>
        <div class="fx-sub">{d.desc}</div>
        {!s.done && d.target > 1 && (
          <Bar
            value={s.progress}
            max={d.target}
            tone="blue"
            label={`${s.progress.toLocaleString('en-US')}/${d.target.toLocaleString('en-US')}`}
          />
        )}
      </div>
      <div class="jn-ach-r">
        <Rewards
          silver={d.reward.silver}
          gold={d.reward.gold}
          items={d.reward.items}
          title={d.reward.title}
        />
        <span class="jn-ach-s">
          {s.done && s.unlockedAt
            ? `Unlocked ${timeAgo(s.unlockedAt, now)}`
            : `+${ACHIEVEMENT_LEGACY_XP} Legacy XP`}
        </span>
      </div>
    </div>
  );
}

function AchievementsTab() {
  const ctx = useCtx();
  const now = useNow(60_000);
  const [cat, setCat] = useState<AchievementCategory | 'All'>('All');
  if (!ctx) return null;
  const states = achievementStates(ctx.a, ctx.c);
  const done = states.filter((s) => s.done).length;
  const list = states
    .filter((s) => cat === 'All' || s.def.cat === cat)
    .sort(
      (x, y) =>
        Number(x.done) - Number(y.done) || y.progress / y.def.target - x.progress / x.def.target,
    );
  return (
    <div class="jn-achs">
      <div class="jn-achs-top">
        <div class="jn-achs-sum">
          <b>{done}</b>
          <span class="fx-dim">/ {states.length} achievements</span>
        </div>
        <Bar value={done} max={states.length} tone="gold" />
        <div class="jn-chips" role="tablist">
          {CATS.map((k) => (
            <button
              key={k}
              class={`jn-chip${cat === k ? ' is-sel' : ''}`}
              role="tab"
              aria-selected={cat === k}
              onClick={() => setCat(k)}
            >
              {k}
            </button>
          ))}
        </div>
      </div>
      <div class="jn-ach-list fx-scroll">
        {list.map((s) => (
          <AchievementRow key={s.def.id} s={s} now={now} />
        ))}
      </div>
    </div>
  );
}

function TitlesTab() {
  const ctx = useCtx();
  if (!ctx) return null;
  const { c, a } = ctx;
  const journals = overworldLayouts().map((l) => ({ id: l.id, name: l.name }));
  // Roster-wide sources, merged into this character's list.
  const owned = [...new Set([...c.titles, ...achievementTitles(a), ...journalTitles(a, journals)])];
  const locked = [
    ...achievementStates(a, c)
      .filter((s) => s.def.reward.title && !owned.includes(s.def.reward.title))
      .map((s) => ({ title: s.def.reward.title as string, src: 'Achievement', how: s.def.desc })),
    ...journals
      .filter((z) => !owned.includes(journalTitle(z.name)))
      .map((z) => ({ title: journalTitle(z.name), src: 'Journal', how: `Complete ${z.name}.` })),
  ];
  const pick = (t: string | null) => {
    if (t && !c.titles.includes(t)) c.titles.push(t);
    if (setActiveTitle(c, t)) {
      toast(t ? `Title set: ${t}` : 'Title cleared', '#eacd95');
      commit();
    }
  };
  return (
    <div class="jn-titles fx-scroll">
      <div class="jn-title-preview">
        <span class="fx-h">Shown above your name</span>
        <div class="jn-nameplate">
          {c.title && <div class="jn-np-title">{c.title}</div>}
          <div class="jn-np-name">{c.name}</div>
        </div>
      </div>
      <div class="jn-title-grid">
        <button class={`jn-title${c.title === null ? ' is-sel' : ''}`} onClick={() => pick(null)}>
          <i />
          <span>No title</span>
        </button>
        {owned.map((t) => (
          <button
            key={t}
            class={`jn-title${c.title === t ? ' is-sel' : ''}`}
            onClick={() => pick(t)}
          >
            <i />
            <span>{t}</span>
          </button>
        ))}
      </div>
      {locked.length > 0 && (
        <>
          <div class="section-h">Not yet earned</div>
          <div class="jn-locked">
            {locked.map((l) => (
              <div key={l.title} class="jn-lock">
                <span class="jn-lock-t">{l.title}</span>
                <span class="fx-dim">
                  <span class="jn-lock-src">{l.src}</span> {l.how}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Panel `journal` (O): Explorer's Journal, achievements and titles. */
export function JournalPanel() {
  const ctx = useCtx();
  const [tab, setTab] = useState<Tab>(
    ui.panelArg.value === 'achievements'
      ? 'achievements'
      : ui.panelArg.value === 'titles'
        ? 'titles'
        : 'journal',
  );
  if (!ctx) return null;
  return (
    <Window
      title="Explorer’s Journal"
      width="min(980px, 96vw)"
      height="min(660px, 88vh)"
      className="fx-win fx-journal"
    >
      <div class="fx-bar-top">
        <div class="fx-tabs" role="tablist">
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'journal'}
            onClick={() => setTab('journal')}
          >
            Journal
          </button>
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'achievements'}
            onClick={() => setTab('achievements')}
          >
            Achievements
          </button>
          <button
            class="tab"
            role="tab"
            aria-selected={tab === 'titles'}
            onClick={() => setTab('titles')}
          >
            Titles
          </button>
        </div>
      </div>
      <div class="jn-body">
        {tab === 'journal' && <JournalTab />}
        {tab === 'achievements' && <AchievementsTab />}
        {tab === 'titles' && <TitlesTab />}
      </div>
      <TipLayer />
    </Window>
  );
}
